import { RecordType } from '../types/api';
/**
 * The identifier a record is shown by.
 *
 * <p>CZNA-977: the system issues ONE identifier series for authority records — the national
 * control number from MARC `001`, stored as `record.export_id`. `record.authority_id`
 * (`AUT-000123`) is an internal surrogate. Measured on 2026-09-02, `authority_id = export_id`
 * held for NONE of the 1 292 908 imported records, so a cataloguer read `AUT-000123456` under
 * the label "ID autority" where NK knows `ph114018`.
 *
 * <p><b>Vocabulary rows are the exception</b> (decided 2026-09-10). The backend mints their
 * `export_id` as `"EXP-" + authority_id` (`VocabularyServiceImpl`), a surrogate of a
 * surrogate. Switching them would put `EXP-VOCAB-LANG-001` on screen where `VOCAB-LANG-001`
 * stands today — strictly worse. Vocabulary types DO appear in the authorities list (they are
 * in its type filter), so this is not theoretical.
 *
 * <p><b>The predicate is the record TYPE, never the value's prefix.</b> Prefix sniffing is
 * what `RecordIdentifierQuery`'s javadoc warns against: the prefix list is open-ended and a
 * miss looks identical to "record not found". `record.type` is a plain String on the wire and
 * also carries ~27 `VOCABULARY_*` codes whose identity is DATA in the `vocabulary_type`
 * registry — they are deliberately NOT members of {@link RecordType} (see its docstring,
 * CZNA-392/393/397). Membership in that enum is therefore the exact mirror of the backend's
 * `RecordType.fromName(type) != null`, and `recordTypeParity.test.ts` keeps the enum pinned to
 * the Java one.
 *
 * <p><b>This module is the only place that decision is taken on the frontend.</b> A second
 * copy is the CZNA-940 finding — `recordDisplayId.test.ts` fails the build if one appears.
 */
var AUTHORITY_TYPES = new Set(Object.values(RecordType));
/**
 * Whether records of this type carry a national control number in `export_id`.
 * Mirrors the backend `RecordIdentifiers.carriesNationalControlNumber`.
 */
export var carriesNationalControlNumber = function (type) {
    return !!type && AUTHORITY_TYPES.has(type);
};
/**
 * The identifier to show the user for this record: the national control number when the
 * record's type has one, otherwise the internal authority id.
 *
 * Returns `null` rather than an empty string when neither is present, so callers can decide
 * whether to render a placeholder or omit the row entirely.
 */
export var recordDisplayId = function (record) {
    var _a, _b;
    var exportId = (_a = record.exportId) === null || _a === void 0 ? void 0 : _a.trim();
    if (carriesNationalControlNumber(record.type) && exportId) {
        return exportId;
    }
    return ((_b = record.authorityId) === null || _b === void 0 ? void 0 : _b.trim()) || null;
};
export default recordDisplayId;
